import { useEffect, useState } from 'react'
import { Button, Dialog, useToast } from '@platform/ui-kit'
import { disablePush, enablePush, pushState } from '@/lib/push'
import type { PushState } from '@/lib/push'

/* ------------------------------------------------------------------
   دروازهٔ فعال‌سازی اعلان — پس از ورود

   ==================================================================
   🔴 اعلانِ داخل برنامه فقط وقتی دیده می‌شود که کاربر برنامه را باز
   کرده باشد — و مدیری که منتظر تأییدِ اوییم، معمولاً بازش نکرده.

   ⚠️ صفحهٔ اعلان‌ها یک دکمهٔ فعال‌سازی داشت، ولی کسی که خبر ندارد
   اعلانی هست، سراغ صفحهٔ اعلان‌ها هم نمی‌رود. دکمه‌ای که پیدا نشود،
   با نبودنش فرقی ندارد.

   ==================================================================
   🔴 «اجبار» اینجا مرزِ سختی دارد که مرورگر می‌گذارد، نه ما.

   ⚠️ اجازهٔ اعلان فقط با کلیکِ واقعیِ کاربر گرفته می‌شود، و اگر یک‌بار
   رد شود دیگر **نمی‌شود دوباره پرسید** — کاربر باید دستی از تنظیمات
   مرورگر مجازش کند. پس مودالی که هیچ راه بستنی نداشته باشد، کاربرِ
   رد‌کرده را برای همیشه بیرونِ پنل نگه می‌دارد.

   🔴 راه‌حل: تا وقتی فعال‌سازی **ممکن** است، راه فراری نیست؛ همین که
   ناممکن شد، مودال به راهنما تبدیل می‌شود و بسته می‌شود.
   ==================================================================
   ------------------------------------------------------------------ */

/*
  ⚠️ حالت‌هایی که فعال‌سازی در آن‌ها اصلاً شدنی نیست.

  🔴 در این‌ها اصرار بی‌معناست: نه مرورگر پشتیبانی می‌کند، نه سرور
  کلید دارد، نه اجازه‌ای مانده که بشود گرفت. مودالِ بی‌راه‌فرار اینجا
  فقط پنل را قفل می‌کند.
*/
const UNREACHABLE: PushState[] = ['unsupported', 'denied', 'disabled_on_server']

/*
  چیزهایی که با فعال‌بودن اعلان به کاربر می‌رسد.

  ⚠️ عمداً همان سه منبعی است که واقعاً Push می‌فرستند — مرخصی،
  جانشینی و میز خدمت.

  🔴 وعدهٔ چیزی که نمی‌آید، بدترین نوع اعلان است: کاربر منتظر می‌ماند،
  خبری نمی‌شود، و دفعهٔ بعد هیچ اعلانی را جدی نمی‌گیرد.
*/
const BENEFITS = [
  { icon: '/icons/3d/page-leave-approvals.png', label: 'درخواست‌هایی که منتظر تأیید شما هستند' },
  { icon: '/icons/3d/leave.png', label: 'پاسخ جانشین به درخواست مرخصی شما' },
  { icon: '/icons/3d/service-desk.png', label: 'تیکت‌های تازه‌ای که به شما ارجاع می‌شود' },
] as const

/** پیام هر حالتِ بن‌بست — کاربر باید بداند دقیقاً چه چیزی را درست کند. */
const DEAD_END_HELP: Record<string, string> = {
  denied:
    'اجازهٔ اعلان در مرورگر شما رد شده است. برای فعال‌کردن، روی نشانِ قفل کنار نشانی سایت بزنید و اعلان‌ها را «مجاز» کنید، سپس صفحه را تازه کنید.',
  disabled_on_server:
    'اعلان روی سرور پیکربندی نشده است. لطفاً با مدیر سامانه تماس بگیرید — تا آن زمان اعلان‌ها را از زنگِ بالای صفحه دنبال کنید.',
  unsupported:
    'مرورگر شما اعلان را پشتیبانی نمی‌کند. با مرورگر به‌روز (مثلاً فایرفاکس یا کروم) وارد شوید تا اعلان‌ها را روی این دستگاه بگیرید.',
}

export function PushEnrollmentGate() {
  const toast = useToast()

  const [state, setState] = useState<PushState | null>(null)
  const [busy, setBusy] = useState(false)

  /*
    ⚠️ کاربری که بن‌بست را خوانده و بسته، نباید در همین نشست دوباره
    همان را ببیند.

    🔴 عمداً در حافظهٔ کامپوننت است و نه `localStorage`: با ورود بعدی
    دوباره پرسیده می‌شود. اگر برای همیشه ذخیره می‌شد، کاربری که بعداً
    اجازه را در مرورگر درست کرده هرگز دعوت نمی‌شد.
  */
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    /*
      ⚠️ وضعیت از خودِ مرورگر پرسیده می‌شود، نه از سرور.

      🔴 سرور فقط می‌داند «اشتراکی ثبت شده»؛ اینکه کاربر بعداً اجازه را
      از تنظیمات پس گرفته یا داده‌های سایت را پاک کرده، نمی‌داند.
    */
    void pushState().then(setState)
  }, [])

  const enable = async () => {
    setBusy(true)

    try {
      const next = await enablePush()

      setState(next)

      if (next === 'subscribed') {
        toast.success('اعلان روی این دستگاه فعال شد.')

        return
      }

      /*
        🔴 حالت‌های بن‌بست پیام خودشان را در مودال می‌گیرند، نه Toast.

        ⚠️ Toast می‌رود؛ ولی کاربری که باید در تنظیمات مرورگر کاری
        انجام دهد، باید متن جلوی چشمش بماند.
      */
    } catch {
      toast.error('فعال‌سازی اعلان ممکن نشد. لطفاً دوباره تلاش کنید.')
    } finally {
      setBusy(false)
    }
  }

  /*
    ⚠️ اشتراکِ نیمه‌کاره پاک می‌شود تا حالت مرورگر و سرور یکی بماند.

    🔴 اگر مرورگر مشترک باشد ولی سرور نداند (خطای شبکه وسط کار)، کاربر
    اعلان نمی‌گیرد و دفعهٔ بعد هم چون مرورگر «مشترک» است، این مودال
    اصلاً باز نمی‌شود — خرابیِ بی‌صدا.
  */
  const retryFromScratch = async () => {
    setBusy(true)

    try {
      await disablePush()

      setState(await pushState())
    } finally {
      setBusy(false)
    }
  }

  /* هنوز نمی‌دانیم — چیزی نشان نده. */
  if (state === null) return null

  /* فعال است — کاری نمانده. */
  if (state === 'subscribed') return null

  /* کاربر بن‌بست را خوانده و بسته است. */
  if (dismissed) return null

  const deadEnd = UNREACHABLE.includes(state)

  return (
    <Dialog
      open
      /*
        🔴 `onClose` فقط در بن‌بست کاری می‌کند.

        ⚠️ `Dialog` همیشه با Escape بسته می‌شود و این تضمینِ
        دسترس‌پذیری است که نباید شکسته شود. پس به‌جای قفل‌کردنِ کلید،
        خودِ بستن بی‌اثر می‌شود: کامپوننت بلافاصله دوباره رندر می‌کند.
      */
      onClose={() => {
        if (deadEnd) setDismissed(true)
      }}
      closeOnOverlayClick={false}
      size="sm"
      title="فعال‌سازی اعلان روی این دستگاه"
      description={
        deadEnd
          ? undefined
          : 'تأییدها و کارهایی که به شما ارجاع می‌شود، باید به‌موقع به دستتان برسد — نه وقتی که اتفاقی سامانه را باز کردید.'
      }
      footer={
        /*
          ⚠️ در موبایل دکمه‌ها تمام‌عرض و زیر هم می‌نشینند.

          🔴 دکمهٔ اصلیِ این مودال تنها راهِ ادامه‌دادن است؛ در عرض کم
          نباید گوشه‌ای کوچک باشد که کاربر دنبالش بگردد.
        */
        deadEnd ? (
          <div className="flex flex-col gap-[var(--pf-space-2)] sm:flex-row sm:justify-end">
            {state === 'denied' && (
              <Button
                variant="secondary"
                loading={busy}
                onClick={() => void retryFromScratch()}
                className="w-full sm:w-auto"
              >
                دوباره بررسی کن
              </Button>
            )}

            <Button
              variant="primary"
              onClick={() => setDismissed(true)}
              className="w-full sm:w-auto"
            >
              متوجه شدم
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            loading={busy}
            onClick={() => void enable()}
            fullWidth
          >
            فعال‌سازی اعلان
          </Button>
        )
      }
    >
      <div className="space-y-[var(--pf-space-5)]">
        {/*
          ⚠️ تصویر روی یک هالهٔ برند می‌نشیند، نه روی زمینهٔ خالی.

          🔴 آیکون‌های سه‌بعدی پس‌زمینهٔ شفاف دارند و رها روی سطحِ
          مودال «چسبانده‌شده» به نظر می‌رسند. حلقهٔ رنگی جایشان را
          تعریف می‌کند و نگاه را اول به همان می‌برد.
        */}
        <div className="flex justify-center">
          <div className="relative flex size-28 items-center justify-center">
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-[var(--pf-brand-50)]"
            />

            {/*
              ⚠️ `alt` خالی و `aria-hidden`: عنوانِ مودال همان را
              می‌گوید و تکرارش با صفحه‌خوان چیزی اضافه نمی‌کند.
            */}
            <img
              src="/icons/3d/notifications.png"
              alt=""
              aria-hidden="true"
              width={96}
              height={96}
              decoding="async"
              className="relative size-24 object-contain"
            />
          </div>
        </div>

        {deadEnd ? (
          /*
            🔴 متنِ بن‌بست در قابِ خودش می‌نشیند.

            ⚠️ این متن یک دستورالعمل است — کاربر باید برود در تنظیمات
            مرورگر کاری بکند. پاراگرافِ ساده در میان بقیهٔ مودال گم
            می‌شد؛ قاب می‌گوید «این را بخوان».
          */
          <p className="rounded-[var(--pf-radius-md)] border border-[var(--pf-border)] bg-[var(--pf-surface-muted)] p-[var(--pf-space-4)] text-[length:var(--pf-text-sm)] leading-relaxed text-[var(--pf-text)]">
            {DEAD_END_HELP[state]}
          </p>
        ) : (
          /*
            ⚠️ هر سطر یک آیکونِ واقعیِ همان ماژول دارد.

            🔴 فهرستِ نقطه‌ای سه خطِ هم‌شکل بود که چشم رویشان سُر
            می‌خورد. آیکونِ ماژول همان چیزی است که کاربر در سایدبار هم
            می‌بیند، پس «این‌ها همان کارهای من‌اند» را بدون توضیح
            می‌رساند.
          */
          <ul className="space-y-[var(--pf-space-2)]">
            {BENEFITS.map((benefit) => (
              <li
                key={benefit.label}
                className="flex items-center gap-[var(--pf-space-3)] rounded-[var(--pf-radius-md)] bg-[var(--pf-surface-muted)] p-[var(--pf-space-3)]"
              >
                <img
                  src={benefit.icon}
                  alt=""
                  aria-hidden="true"
                  width={32}
                  height={32}
                  loading="lazy"
                  decoding="async"
                  className="size-8 shrink-0 object-contain"
                />

                <span className="text-[length:var(--pf-text-sm)] leading-relaxed text-[var(--pf-text)]">
                  {benefit.label}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  )
}
